import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import * as fileDownload from "js-file-download"
import { useState } from "react"
import { FileIcon, defaultStyles } from "react-file-icon"

import { axiosClient } from "../../../../../common/axios"
import Column, {
  CenterEverythingColumn,
} from "../../../../../common/components/Column"
import Row from "../../../../../common/components/Row"
import { trimLongString, useIsMobile } from "../../../../../common/utils"
import { addCRMEventToCandidate, getFileExtension } from "../../../utils"
import { EVENT_TYPES } from "../../data"
import { AccordionSummaryContent, StyledAccordion } from "./Accordions"
import { performedBySubtitle } from "./PerformedByAttribution"
import TabColumn from "./TabColumn"
import TabContentColumn from "./TabContentColumn"
import TabHeaderRow from "./TabHeaderRow"
import UploadFileDialog from "./UploadFileDialog"
import ActivitySkeleton from "./components/ActivitySkeleton"

export default function FilesTab({
  candidate,
  setCandidate,
  isLoading,
  readOnly = false,
}) {
  const [uploadFileDialogOpen, setUploadFileDialogOpen] = useState(false)
  const events = candidate.crm_events.filter(
    (event) => event.type === EVENT_TYPES.fileUpload
  )

  function handleUploadFileDialogClosed(fileUploadEvent) {
    if (fileUploadEvent) {
      addCRMEventToCandidate(
        setCandidate,
        fileUploadEvent,
        EVENT_TYPES.fileUpload
      )
    }
    setUploadFileDialogOpen(false)
  }

  return (
    <TabColumn>
      <TabHeaderRow title="Files History">
        {readOnly ? null : (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<InsertDriveFileOutlinedIcon />}
            onClick={() => setUploadFileDialogOpen(true)}
          >
            Add File
          </Button>
        )}
        <UploadFileDialog
          open={uploadFileDialogOpen}
          onClose={handleUploadFileDialogClosed}
          candidate={candidate}
        />
      </TabHeaderRow>
      <TabContentColumn>
        {isLoading ? (
          <ActivitySkeleton />
        ) : events.length === 0 ? (
          <CenterEverythingColumn>
            <Typography variant="body1" color="text.secondary">
              No files added yet.
            </Typography>
          </CenterEverythingColumn>
        ) : (
          events.map((fileEvent) => (
            <StyledAccordion key={fileEvent.id}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <AccordionSummaryContent
                  accordionTitle="Added File"
                  subtitle={performedBySubtitle(fileEvent)}
                  Icon={<InsertDriveFileOutlinedIcon />}
                  dateStr={fileEvent.created}
                />
              </AccordionSummary>
              <AccordionDetails>
                <FileContainer>
                  <FileRepresentation fileEvent={fileEvent} />
                </FileContainer>
              </AccordionDetails>
            </StyledAccordion>
          ))
        )}
      </TabContentColumn>
    </TabColumn>
  )
}

export function FileRepresentation({ fileEvent }) {
  const isMobile = useIsMobile()
  function getFullFileName(fileEvent) {
    return fileEvent.file_name.split("/").pop()
  }

  function getFileName(fileEvent) {
    return trimLongString(getFullFileName(fileEvent), isMobile ? 20 : 26)
  }

  function getFileSize(fileEvent) {
    const kiloBytes = 1024
    const megaBytes = kiloBytes * kiloBytes
    const fileSize = fileEvent.file_size
    let suffix, divisor
    if (fileSize >= megaBytes) {
      suffix = "MB"
      divisor = megaBytes
    } else if (fileSize >= kiloBytes) {
      suffix = "KB"
      divisor = kiloBytes
    } else {
      suffix = "Bytes"
      divisor = 1
    }
    return `${Math.floor(fileSize / divisor).toLocaleString()} ${suffix}`
  }

  function downloadFileEvent(fileEvent) {
    return () => {
      axiosClient
        .get(fileEvent.file, { responseType: "blob" })
        .then(({ data }) => fileDownload(data, getFullFileName(fileEvent)))
    }
  }

  return (
    <Row
      justifyContent="space-between"
      alignItems="center"
      gap={{ xs: 1, sm: 2 }}
      width="100%"
    >
      <Row gap={{ xs: 1, sm: 2 }}>
        <Row sx={{ width: "32px" }}>
          <FileIcon
            extension={getFileExtension(fileEvent.file_name)}
            {...defaultStyles[getFileExtension(fileEvent.file_name)]}
          />
        </Row>
        <Column>
          <Typography variant="body1" fontWeight={600} color="text.secondary">
            {getFileName(fileEvent)}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getFileSize(fileEvent)}
          </Typography>
        </Column>
      </Row>
      <IconButton
        color="inherit"
        size="small"
        onClick={downloadFileEvent(fileEvent)}
      >
        <FileDownloadOutlinedIcon fontSize="medium" />
      </IconButton>
    </Row>
  )
}

const FileContainer = styled(Row)(({ theme }) => ({
  alignItems: "center",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.colors.gray50,
  borderRadius: "8px",
  maxWidth: "fit-content",
}))
